import { useCallback, useEffect, useRef, useState } from "react";
import { planToA4Pdf } from "@/lib/a4pdf";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Printer, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CleanPlan, svgToPng } from "@/components/CleanPlan";
import { useJob } from "@/components/PlanTools";
import { tableLabel } from "@/components/FloorMap";
import { API, api, errMsg } from "@/lib/api";

const LOGO = { "AFRODITE BOUTIQUE HOTEL": "afrodite", "TENUTA LEDO": "ledo", "CATERING PRESSO SEDE ESTERNA": "catering" };

function Sheet({ plan, ia, staff, clone, children }) {
  const seats = plan.tables.reduce((s, t) => s + Number(t.posti || 0), 0);
  const cop = staff ? Object.values(staff).reduce((s, c) => s + c.n, 0) : 0;
  return (
    <div id={clone ? "print-area" : undefined} className="bg-white rounded-xl border border-[#D4E6EC] p-6 sm:p-10 space-y-4" data-testid={clone ? undefined : "print-sheet"}>
      <div className="flex items-end justify-between gap-4 border-b border-[#0D2B3A] pb-3">
        <div>
          <h1 className="font-serif-it text-3xl sm:text-4xl text-[#0D2B3A]" data-testid="print-title">{plan.titolo || plan.nome}</h1>
          <p className="text-sm text-[#5A7380]">{plan.sottotitolo || plan.sede} • {plan.tables.length} tavoli • {staff ? <b className="text-[#B45309]" data-testid="print-total-coperti">{cop} pax</b> : `${seats} posti`}</p>
        </div>
        {LOGO[plan.sede] && <img src={`${API}/public/logo/${LOGO[plan.sede]}`} alt="" className="h-12 w-auto object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} />}
      </div>
      {children}
      <p className="text-[11px] text-[#7B929C]">{staff ? "Mappa per il personale di sala • numero tavolo x pax" : ia ? "Illustrazione indicativa, non in scala" : "Planimetria indicativa in scala • misure da verificare sul posto"}</p>
    </div>
  );
}

const itDate = (iso) => (iso ? iso.split("-").reverse().join("/") : "");

function copertiOf(plan) {
  const by = {};
  (plan.guests || []).forEach((g) => {
    if (!g.tavolo || g.tipo === "fornitore") return;
    const k = by[g.tavolo] || (by[g.tavolo] = { n: 0, b: 0 });
    k.n += 1;
    if (g.tipo === "bambino") k.b += 1;
  });
  return Object.fromEntries(plan.tables.map((t) => {
    const g = by[tableLabel(t)];
    const n = g ? g.n : Number(t.posti || 0);
    return [t.id, { n, text: String(n) }];
  }));
}

export default function PlanPrint() {
  const { id } = useParams();
  const cid = useSearchParams()[0].get("contratto");
  const [plan, setPlan] = useState(null);
  const [mode, setMode] = useState("pulita");
  const [aud, setAud] = useState("sposi");
  const [pdfBusy, setPdfBusy] = useState(false);
  const [amb, setAmb] = useState("esterna");
  const [starting, setStarting] = useState(false);
  const svg = useRef(null);
  const [maskUrl, setMaskUrl] = useState("");
  const maskAt = plan?.analisi?.mask?.at;
  useEffect(() => {
    if (!maskAt) return;
    api.get(`/floorplans/${id}/mask?stampa=1&v=${maskAt}`, { responseType: "blob" }).then((r) => {
      const fr = new FileReader();
      fr.onload = () => setMaskUrl(fr.result);
      fr.readAsDataURL(r.data);
    });
  }, [id, maskAt]);
  const fetchPlan = useCallback(async () => {
    if (!cid) return (await api.get("/floorplans")).data.find((x) => x.id === id) || false;
    const s = (await api.get(`/contracts/${cid}/seating`)).data;
    if (!s.plan) return false;
    return { ...s.plan, guests: s.guests, illustrazione_at: s.illustrazione_at, titolo: s.evento.cliente_nome || s.plan.nome, sottotitolo: `${s.evento.evento_tipo || "Evento"} • ${itDate(s.evento.evento_data)} • ${s.plan.nome}` };
  }, [id, cid]);
  useEffect(() => {
    fetchPlan().then((p) => {
      setPlan(p);
      if (p?.ambiente) setAmb(p.ambiente);
      if (p?.illustrazione_at) setMode("ia");
    });
  }, [fetchPlan]);
  const running = useJob(plan, cid ? `illustrazione_${cid}` : "illustrazione", (p) => { setPlan(p); if (p.illustrazione_at) setMode("ia"); }, "Versione illustrata pronta", fetchPlan);
  const busy = starting || running;
  const illustrate = async () => {
    setStarting(true);
    try {
      const fd = new FormData();
      fd.append("file", await svgToPng(svg.current), "pianta.png");
      fd.append("ambiente", amb);
      if (cid) fd.append("cid", cid);
      const r = (await api.post(`/floorplans/${id}/illustrate`, fd)).data;
      setPlan({ ...plan, job: r.job });
    } catch (e) {
      toast.error(errMsg(e));
    }
    setStarting(false);
  };
  if (plan === null) return <div className="p-6 text-sm">Caricamento…</div>;
  if (!plan) return <div className="p-6 text-sm">Mappa non trovata</div>;
  const illUrl = cid ? `${API}/contracts/${cid}/seating/illustration` : `${API}/floorplans/${id}/illustration`;
  const staff = aud === "staff" ? copertiOf(plan) : null;
  const show = staff ? "pulita" : mode;
  const download = async () => {
    setPdfBusy(true);
    try {
      const seats = plan.tables.reduce((s, t) => s + Number(t.posti || 0), 0);
      const pax = staff ? Object.values(staff).reduce((s, c) => s + c.n, 0) : 0;
      const title = plan.titolo || plan.nome;
      await planToA4Pdf({
        mapBlob: show === "pulita" ? await svgToPng(svg.current, 3400) : null,
        mapUrl: cid ? `/contracts/${cid}/seating/illustration?v=${plan.illustrazione_at}` : `/floorplans/${id}/illustration?v=${plan.illustrazione_at}`,
        title,
        subtitle: `${plan.sottotitolo || plan.sede} • ${plan.tables.length} tavoli • ${staff ? `${pax} pax` : `${seats} posti`}`,
        note: staff ? "Mappa per il personale di sala • numero tavolo x pax" : show === "ia" ? "Illustrazione indicativa, non in scala" : "Planimetria indicativa in scala • misure da verificare sul posto",
        logoUrl: LOGO[plan.sede] ? `/public/logo/${LOGO[plan.sede]}` : "",
        fileName: `Mappa ${title}${staff ? " - personale" : ""}.pdf`,
      });
    } catch (e) {
      toast.error("PDF non riuscito, riprova");
    }
    setPdfBusy(false);
  };
  const body = (main) => (
    <>
      <div className={show === "pulita" ? "" : "hidden"}><CleanPlan plan={plan} tables={plan.tables} interior={amb === "interna"} svgRef={main ? svg : undefined} maskUrl={maskUrl} coperti={staff && Object.fromEntries(Object.entries(staff).map(([k, c]) => [k, c.text]))} /></div>
      {show === "ia" && (plan.illustrazione_at ? <img src={`${illUrl}?v=${plan.illustrazione_at}`} alt="Planimetria illustrata" className="w-full rounded" data-testid={main ? "print-ai-image" : undefined} />
        : main && <p className="text-sm text-[#7B929C] py-16 text-center" data-testid="print-ai-empty">Premi «Crea versione illustrata»: l'IA ridipinge la pianta pulita in stile acquerello. Le misure possono essere meno precise.</p>)}
    </>
  );
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 print:hidden">
        <Link to={cid ? `/contratti/${cid}` : "/sale"} className="text-sm text-[#0B6E8F] inline-flex items-center gap-1 mr-auto" data-testid="print-back"><ArrowLeft size={14} /> {cid ? "Contratto" : "Sale"}</Link>
        <div className="inline-flex rounded-full border border-[#D4E6EC] p-1 bg-white">
          {[["sposi", "Per gli sposi"], ["staff", "Per il personale"]].map(([k, l]) => <button key={k} onClick={() => setAud(k)} className={`px-3 py-1.5 rounded-full text-sm transition-colors ${aud === k ? "bg-[#B45309] text-white" : ""}`} data-testid={`print-aud-${k}`}>{l}</button>)}
        </div>
        {!staff && <div className="inline-flex rounded-full border border-[#D4E6EC] p-1 bg-white">
          {[["pulita", "Pulita in scala"], ["ia", "Illustrata IA"]].map(([k, l]) => <button key={k} onClick={() => setMode(k)} className={`px-3 py-1.5 rounded-full text-sm transition-colors ${mode === k ? "bg-[#0D2B3A] text-white" : ""}`} data-testid={`print-mode-${k}`}>{l}</button>)}
        </div>}
        {!staff && <div className="inline-flex rounded-full border border-[#D4E6EC] p-1 bg-white" data-testid="print-ambiente">
          {[["esterna", "Esterna"], ["interna", "Interna"]].map(([k, l]) => <button key={k} onClick={() => setAmb(k)} className={`px-3 py-1.5 rounded-full text-sm transition-colors ${amb === k ? "bg-[#34B3B0] text-white" : ""}`} data-testid={`print-amb-${k}`}>{l}</button>)}
        </div>}
        {!staff && <Button variant="outline" className="rounded-full" disabled={busy} onClick={illustrate} data-testid="print-illustrate">{busy ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Sparkles size={14} className="mr-1" />}{busy ? "Disegno… (fino a 1 minuto)" : plan.illustrazione_at ? "Rifai con IA" : "Crea versione illustrata"}</Button>}
        <Button className="rounded-full" disabled={pdfBusy} onClick={download} data-testid="print-btn">{pdfBusy ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Printer size={14} className="mr-1" />} PDF A4 da stampare</Button>
      </div>
      <Sheet plan={plan} ia={show === "ia"} staff={staff}>
        {body(true)}
      </Sheet>
    </div>
  );
}
